import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";

import AccountInfo from "./AccountInfo";
import GeneralSettings from "./GeneralSettings";
import Billing from "./Billing";
import Integration from "./Integration";
import MyTeam from "./MyTeam";
import ScoringConfiguration from "./ScoringConfiguration";
import ScreeningQuestions from "./ScreeningQuestions";
import StatusConfiguration from "./StatusConfiguration";
import ProposalTemplates from "./Templates/ProposalTemplates";
import ProposalSections from "./Templates/ProposalSections";
import SettingsNav from "./settings/SettingsNav";
import { SettingsContext } from "./settings/ui";
import api from "../chat/api";
import useCurrentOrgRole from "../../utils/useCurrentOrgRole";

const Account = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [workspaces, setWorkspaces] = useState([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(true);
  const [workspacesError, setWorkspacesError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedOrgId, setSelectedOrgId] = useState(() => Cookies.get("selected_org_id"));
  const [overlayEl, setOverlayEl] = useState(null);
  const [dirty, setDirty] = useState(false);

  const getActiveId = () => {
    const p = location.pathname;
    if (p.includes("/billing")) return "billing";
    if (p.includes("/integration")) return "integrations";
    if (p.includes("/general")) return "general";
    if (p.includes("/team/configuration")) return "scoring";
    if (p.includes("/team/screening-questions")) return "screening";
    if (p.includes("/status")) return "statuses";
    if (p.includes("/proposal-sections")) return "sections";
    if (p.includes("/templates")) return "design";
    if (p.includes("/team")) return "members";
    return "profile";
  };

  const activeId = getActiveId();

  const memberRole = useCurrentOrgRole();

  useEffect(() => {
    const initWorkspaces = async () => {
      setLoadingWorkspaces(true);
      setWorkspacesError(false);
      try {
        const token = Cookies.get("access_token");
        if (!token) return;
        const response = await api.get("/api/v1/org/");
        if (response.data && Array.isArray(response.data)) {
          setWorkspaces(response.data);
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
        setWorkspacesError(true);
      } finally {
        setLoadingWorkspaces(false);
      }
    };

    initWorkspaces();
    const onWorkspaceChange = () => {
      setSelectedOrgId(Cookies.get("selected_org_id"));
      initWorkspaces();
    };
    const onWorkspaceRenamed = (e) => {
      const { id, name } = e.detail || {};
      setWorkspaces((list) => list.map((w) => (String(w?.id) === String(id) ? { ...w, name } : w)));
    };
    window.addEventListener("workspace-changed", onWorkspaceChange);
    window.addEventListener("workspace-renamed", onWorkspaceRenamed);
    return () => {
      window.removeEventListener("workspace-changed", onWorkspaceChange);
      window.removeEventListener("workspace-renamed", onWorkspaceRenamed);
    };
  }, [reloadKey]);

  const activeWorkspace = useMemo(
    () => workspaces.find((w) => String(w?.id) === String(selectedOrgId)) || null,
    [workspaces, selectedOrgId]
  );

  // Admins are "Owner" in permission_role. Fall back to the workspace list's permission
  // when the member lookup fails, and treat an unknown role as not admin.
  const orgRole = useMemo(() => {
    if (memberRole) return memberRole;
    const perm = String(activeWorkspace?.permission || "").toLowerCase();
    if (perm === "owner" || perm === "admin") return "Owner";
    return null;
  }, [memberRole, activeWorkspace]);
  const roleKnown = orgRole !== null;
  const canManage = orgRole === "Owner";

  const targetWorkspace = activeWorkspace;
  const goNoGoDisabled = !targetWorkspace || loadingWorkspaces;

  const goNoGoPath = (id) =>
    `/account/team/${id === "scoring" ? "configuration" : "screening-questions"}/${targetWorkspace.id}`;

  useEffect(() => {
    if ((activeId === "scoring" || activeId === "screening") && targetWorkspace?.id) {
      const urlId = location.pathname.split("/").filter(Boolean).pop();
      if (urlId !== String(targetWorkspace.id)) navigate(goNoGoPath(activeId), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, targetWorkspace?.id, location.pathname]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const handleSelect = (id) => {
    if (id === activeId) return null;
    if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return null;
    switch (id) {
      case "profile":
        return navigate("/account");
      case "general":
        return navigate("/account/general");
      case "members":
        return navigate("/account/team");
      case "scoring":
      case "screening":
        if (!targetWorkspace?.id) return toast.error("Workspace not found");
        return navigate(goNoGoPath(id));
      case "statuses":
        return navigate("/account/status");
      case "design":
        return navigate("/account/templates");
      case "sections":
        return navigate("/account/proposal-sections");
      case "billing":
        return navigate("/account/billing");
      case "integrations":
        return navigate("/account/integration");
      default:
        return null;
    }
  };


  const contextValue = useMemo(
    () => ({
      workspaceName: activeWorkspace?.name || "",
      activeWorkspace,
      orgRole,
      roleKnown,
      canManage,
      overlayEl,
      setDirty,
    }),
    [activeWorkspace, orgRole, roleKnown, canManage, overlayEl]
  );

  const WORKSPACE_PAGES = ["general", "members", "scoring", "screening", "statuses", "sections"];
  const needsWorkspace = WORKSPACE_PAGES.includes(activeId) && !loadingWorkspaces && !activeWorkspace;
  const urlWorkspaceId = location.pathname.split("/").filter(Boolean).pop();
  const awaitingRedirect =
    (activeId === "scoring" || activeId === "screening") &&
    (!targetWorkspace || urlWorkspaceId !== String(targetWorkspace.id));

  return (
    <SettingsContext.Provider value={contextValue}>
      <div className="w-full h-full p-4 md:p-6 bg-[#f8f9fa] box-border">
        <div className="flex flex-col md:flex-row w-full min-h-[calc(100vh-3rem)] md:h-[calc(100vh-3rem)] bg-[#FEFDFD] border border-black/10 rounded-[16px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="w-full md:w-[256px] shrink-0 border-b md:border-b-0 md:border-r border-black/10 py-6 px-3 overflow-y-auto">
            <SettingsNav
              activeId={activeId}
              onSelect={handleSelect}
              canManage={canManage}
              roleKnown={roleKnown}
              orgRole={orgRole}
              disabledIds={goNoGoDisabled ? ["scoring", "screening"] : []}
            />
          </div>

          <div className="relative flex-1 min-w-0 flex flex-col">
            <div className={`flex-1 min-h-0 overflow-y-auto px-4 pt-6 md:px-10 md:pt-8 ${activeId === "profile" || activeId === "general" ? "pb-24" : "pb-8"}`}>
              {awaitingRedirect && !needsWorkspace ? null : needsWorkspace && workspacesError ? (
                <div className="max-w-[520px] mx-auto mt-14 flex flex-col items-center text-center gap-3 font-outfit">
                  <div className="text-[18px] font-semibold text-[#18101C]">We couldn't load your workspaces</div>
                  <button
                    type="button"
                    onClick={() => setReloadKey((k) => k + 1)}
                    className="h-9 px-3.5 font-outfit text-[14px] font-medium text-[#3F3F46] bg-white border border-black/20 rounded-[8px] hover:bg-[#F9FAFB]"
                  >
                    Try again
                  </button>
                </div>
              ) : needsWorkspace ? (
                <div className="max-w-[520px] mx-auto mt-14 flex flex-col items-center text-center gap-3 font-outfit">
                  <div className="text-[18px] font-semibold text-[#18101C]">
                    {workspaces.length ? "No workspace selected" : "You're not in a workspace yet"}
                  </div>
                  <div className="text-[14px] leading-normal text-[#71717A]">
                    {workspaces.length
                      ? "Pick a workspace from the switcher in the sidebar to see its settings."
                      : "Your workspace is still being set up. Refresh the page in a moment, or ask an admin to invite you."}
                  </div>
                </div>
              ) : (
                <Routes>
                  <Route path="/" element={<AccountInfo />} />
                  <Route path="/general" element={<GeneralSettings />} />
                  <Route path="/team/configuration/:workspaceId" element={<ScoringConfiguration key={location.pathname} />} />
                  <Route path="/team/screening-questions/:workspaceId" element={<ScreeningQuestions key={location.pathname} />} />
                  <Route path="/status" element={<StatusConfiguration />} />
                  <Route path="/templates" element={<ProposalTemplates />} />
                  <Route path="/proposal-sections" element={<ProposalSections />} />
                  <Route path="/team/*" element={<MyTeam />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/integration" element={<Integration />} />
                  <Route path="*" element={<Navigate to="/account" replace />} />
                </Routes>
              )}
            </div>
            <div ref={setOverlayEl} className="pointer-events-none absolute inset-0" />
          </div>
        </div>
      </div>
    </SettingsContext.Provider>
  );
};

export default Account;
